import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  author: string;
  excerpt?: string;
  content: string;
  featuredImage?: string;
  category: string;
  tags?: string;
  isPublished: boolean;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

const BlogAdminPage: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);
  
  const [formData, setFormData] = useState<Partial<BlogPost>>({
    title: '',
    slug: '',
    author: '',
    excerpt: '',
    content: '',
    featuredImage: '',
    category: 'General',
    tags: '',
    isPublished: false
  });
  
  // Tiptap editor
  const editor = useEditor({
    extensions: [StarterKit],
    content: formData.content || '',
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    }
  });
  
  // Update editor content when formData changes
  useEffect(() => {
    if (editor && formData.content) {
      editor.commands.setContent(formData.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingPost, isCreating]);
  
  // Check if already logged in
  useEffect(() => {
    const adminAuth = sessionStorage.getItem('adminAuth');
    if (adminAuth === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);
  
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'Alert1Admin2025!') {
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'authenticated');
      setLoginError('');
    } else {
      setLoginError('Invalid password');
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  const categories = [
    'Alert1 Blog',
    'Family Caregivers',
    'Resources',
    'Elder Care Planning',
    'General',
    'Senior Safety & Independence',
    'Fall Prevention',
    'Recipes'
  ];

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog-posts?limit=1000`);
      const data = await response.json();
      setPosts(data.posts || []);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchPosts();
    }
  }, [isAuthenticated]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    setFormData(prev => ({
      ...prev,
      title,
      slug: generateSlug(title)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = editingPost 
        ? `${API_URL}/api/blog-posts/${editingPost.id}`
        : `${API_URL}/api/blog-posts`;
      
      const method = editingPost ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Error: ${error.error}`);
        return;
      }

      alert(editingPost ? 'Blog post updated successfully!' : 'Blog post created successfully!');
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Error saving blog post:', error);
      alert('Failed to save blog post');
    }
  };

  const handleEdit = (post: BlogPost) => {
    console.log('[EDIT] Editing post:', post.title);
    setEditingPost(post);
    setFormData(post);
    setIsCreating(false);
    console.log('[EDIT] State updated - editingPost:', post, 'isCreating:', false);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) {
      return;
    }

    try {
      await fetch(`${API_URL}/api/blog-posts/${id}`, {
        method: 'DELETE'
      });
      
      alert('Blog post deleted successfully!');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      alert('Failed to delete blog post');
    }
  };

  const handleTogglePublish = async (post: BlogPost) => {
    try {
      const response = await fetch(`${API_URL}/api/blog-posts/${post.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...post,
          isPublished: !post.isPublished,
          publishedAt: !post.isPublished ? new Date().toISOString() : post.publishedAt
        })
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Error: ${error.error}`);
        return;
      }

      fetchPosts();
    } catch (error) {
      console.error('Error toggling publish status:', error);
      alert('Failed to update publish status');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      author: '',
      excerpt: '',
      content: '',
      featuredImage: '',
      category: 'General',
      tags: '',
      isPublished: false
    });
    setEditingPost(null);
    setIsCreating(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login-container">
          <div className="admin-login-box">
            <h1>Blog Admin Login</h1>
            <form onSubmit={handleLogin}>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
              </div>
              {loginError && <div className="error-message">{loginError}</div>}
              <button type="submit" className="btn btn-primary">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>Blog Post Management</h1>
        <button onClick={handleLogout} className="btn btn-secondary" style={{ marginRight: '1rem' }}>
          Logout
        </button>
        <Link to="/" className="btn btn-secondary">Back to Site</Link>
      </div>

      <div className="admin-content">
        {/* Create/Edit Form */}
        <div className="admin-section">
          <div className="section-header">
            <h2>{editingPost ? 'Edit Blog Post' : isCreating ? 'Create New Blog Post' : 'Blog Posts'}</h2>
            {!isCreating && !editingPost && (
              <button 
                className="btn btn-primary"
                onClick={() => {
                  console.log('[CREATE] Setting isCreating to true');
                  setIsCreating(true);
                }}
              >
                Create New Post
              </button>
            )}
            {(isCreating || editingPost) && (
              <button 
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>

          {(isCreating || editingPost) && (
            <form onSubmit={handleSubmit} className="blog-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleTitleChange}
                    required
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="slug">Slug *</label>
                  <input
                    type="text"
                    id="slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  />
                  <small>URL-friendly version (auto-generated from title)</small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="author">Author *</label>
                  <input
                    type="text"
                    id="author"
                    name="author"
                    value={formData.author}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="category">Category *</label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="excerpt">Excerpt</label>
                  <textarea
                    id="excerpt"
                    name="excerpt"
                    value={formData.excerpt}
                    onChange={handleInputChange}
                    rows={3}
                    className="form-control"
                    placeholder="Short summary of the blog post..."
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label htmlFor="content">Content *</label>
                    <button
                      type="button"
                      onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                      style={{
                        fontSize: '12px',
                        padding: '4px 12px',
                        background: showHtmlEditor ? '#ff9800' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    >
                      {showHtmlEditor ? '📝 Rich Text' : '🔧 HTML'}
                    </button>
                  </div>
                  {showHtmlEditor ? (
                  <textarea
                    id="content"
                    name="content"
                    value={formData.content}
                    onChange={handleInputChange}
                    rows={15}
                    required
                    className="form-control"
                      placeholder="Edit raw HTML..."
                    />
                  ) : (
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                      {editor && (
                        <>
                          <div style={{ 
                            borderBottom: '1px solid #ddd', 
                            padding: '8px', 
                            background: '#f8f9fa',
                            display: 'flex',
                            gap: '4px',
                            flexWrap: 'wrap'
                          }}>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleBold().run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('bold') ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('bold') ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              <strong>B</strong>
                            </button>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleItalic().run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('italic') ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('italic') ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontStyle: 'italic'
                              }}
                            >
                              I
                            </button>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('heading', { level: 1 }) ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('heading', { level: 1 }) ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              H1
                            </button>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('heading', { level: 2 }) ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('heading', { level: 2 }) ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              H2
                            </button>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleBulletList().run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('bulletList') ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('bulletList') ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              •
                            </button>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleOrderedList().run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('orderedList') ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('orderedList') ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              1.
                            </button>
                            <button
                              type="button"
                              onClick={() => editor.chain().focus().toggleBlockquote().run()}
                              style={{
                                padding: '6px 10px',
                                border: editor.isActive('blockquote') ? '2px solid #007bff' : '1px solid #ddd',
                                background: editor.isActive('blockquote') ? '#e7f3ff' : 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                              }}
                            >
                              Quote
                            </button>
                          </div>
                        </>
                      )}
                      <div style={{ minHeight: '300px', padding: '1rem' }}>
                        {editor ? <EditorContent editor={editor} /> : 'Loading editor...'}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="featuredImage">Featured Image URL</label>
                  <input
                    type="text"
                    id="featuredImage"
                    name="featuredImage"
                    value={formData.featuredImage}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="https://example.com/image.jpg"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="tags">Tags</label>
                  <input
                    type="text"
                    id="tags"
                    name="tags"
                    value={formData.tags}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
              </div>

              <div className="form-actions">
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button type="submit" className="btn btn-primary">
                  {editingPost ? 'Update Post' : 'Create Post'}
                </button>
                  
                  {editingPost && (
                    <button
                      type="button"
                      className={`btn ${formData.isPublished ? 'btn-warning' : 'btn-success'}`}
                      onClick={async (e) => {
                        e.preventDefault();
                        const response = await fetch(`${API_URL}/api/blog-posts/${editingPost.id}`, {
                          method: 'PUT',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({
                            ...formData,
                            isPublished: !formData.isPublished,
                            publishedAt: !formData.isPublished ? new Date().toISOString() : formData.publishedAt
                          })
                        });

                        if (response.ok) {
                          alert(formData.isPublished ? 'Post unpublished successfully!' : 'Post published successfully!');
                          fetchPosts();
                          resetForm();
                        } else {
                          alert('Failed to update publish status');
                        }
                      }}
                    >
                      {formData.isPublished ? '✓ Unpublish' : '✓ Publish Now'}
                    </button>
                  )}
                  
                  {isCreating && (
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={async (e) => {
                        e.preventDefault();
                        const submitData = { ...formData, isPublished: true, publishedAt: new Date().toISOString() };
                        const response = await fetch(`${API_URL}/api/blog-posts`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify(submitData)
                        });

                        if (!response.ok) {
                          const error = await response.json();
                          alert(`Error: ${error.error}`);
                          return;
                        }

                        alert('Blog post created and published!');
                        resetForm();
                        fetchPosts();
                      }}
                    >
                      ✓ Save & Publish
                    </button>
                  )}
                  
                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                  Cancel
                </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* Blog Posts List */}
        {!isCreating && !editingPost && (
          <div className="admin-section">
            {loading ? (
              <p>Loading blog posts...</p>
            ) : posts.length === 0 ? (
              <p>No blog posts found. Create your first post!</p>
            ) : (
              <div className="table-container">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Author</th>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map(post => (
                      <tr key={post.id}>
                        <td>
                          <strong>{post.title}</strong>
                          <br />
                          <small>/{post.slug}</small>
                        </td>
                        <td>{post.author}</td>
                        <td>{post.category}</td>
                        <td>
                          <span className={`status-badge ${post.isPublished ? 'published' : 'draft'}`}>
                            {post.isPublished ? 'Published' : 'Draft'}
                          </span>
                        </td>
                        <td>{formatDate(post.createdAt)}</td>
                        <td>
                          <div className="action-buttons">
                            <a
                              href={`/blog/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-sm btn-secondary"
                              style={{ display: 'inline-block', textDecoration: 'none', marginRight: '5px' }}
                            >
                              👁️ View
                            </a>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleEdit(post)}
                            >
                              Edit
                            </button>
                            <button
                              className={`btn btn-sm ${post.isPublished ? 'btn-warning' : 'btn-success'}`}
                              onClick={() => handleTogglePublish(post)}
                              title={post.isPublished ? 'Unpublish this post' : 'Publish this post'}
                            >
                              {post.isPublished ? 'Unpublish' : 'Publish'}
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(post.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogAdminPage;

